import { DeepLink } from "./DeepLink";
import { Cookie } from "../services/storage";
import { PENDING_DEEP_LINK, REQUEST_NUMBER } from "../constants/storage";

/**
 * Static utility class for persistence of pending deeplinks.
 *
 * Uses localStorage (PENDING_DEEP_LINK key) for the canonical DeepLink object,
 * and REQUEST_NUMBER cookie for backend integration.
 *
 * localStorage is used instead of sessionStorage because the SA login flow
 * redirects to an external Samsung Account portal (different domain), which
 * clears sessionStorage. localStorage persists across the redirect.
 *
 * No URL parsing logic.
 */
export class DeepLinkStore {
  /**
   * Serializes a DeepLink to localStorage under PENDING_DEEP_LINK key.
   * If the deeplink has a ticket, also writes REQUEST_NUMBER cookie.
   *
   * @param deepLink The deeplink to persist
   */
  static save(deepLink: DeepLink): void {
    localStorage.setItem(PENDING_DEEP_LINK, JSON.stringify(deepLink));

    if (deepLink.ticket) {
      Cookie.set(REQUEST_NUMBER, deepLink.ticket);
    }
  }

  /**
   * Reads and deserializes PENDING_DEEP_LINK from localStorage.
   *
   * @returns The pending DeepLink, or null if absent or parse error
   */
  static get(): DeepLink | null {
    const raw = localStorage.getItem(PENDING_DEEP_LINK);
    if (!raw) return null;

    try {
      return JSON.parse(raw) as DeepLink;
    } catch {
      return null;
    }
  }

  /**
   * Returns the current pending deeplink and removes PENDING_DEEP_LINK from localStorage.
   * Does NOT remove the REQUEST_NUMBER cookie (that is cleared by the consumer
   * after use, e.g., RequestDetailsModal, or via clear()).
   *
   * @returns The pending DeepLink, or null if absent
   */
  static consume(): DeepLink | null {
    const deepLink = DeepLinkStore.get();
    localStorage.removeItem(PENDING_DEEP_LINK);
    return deepLink;
  }

  /**
   * Removes both PENDING_DEEP_LINK from localStorage AND REQUEST_NUMBER cookie.
   * Used for full cleanup (logout, idle timeout, etc.).
   */
  static clear(): void {
    localStorage.removeItem(PENDING_DEEP_LINK);
    Cookie.remove(REQUEST_NUMBER);
  }
}
